import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { AsnImportDrawer } from './csv-import/asn-import-drawer';
import { useSelectedProductEntities } from './hooks';
import { messages } from './messages';
import { SelectProductsTable } from './select-products-table/select-products-table';
import { ImportStatus } from '../../../../../../../components/csv-import/data/types/import-status';
import { ImportType } from '../../../../../../../components/csv-import/data/types/import-type';
import { useModal } from '../../../../../../../hooks/use-modal';
import { ShipStationFulfillmentBarcode } from '../../../../../../../products/product-details/constants/product-options';
import Global from '../../../../../../../store/global-selectors';
import { bulkLoadProductData } from '../../../../../../../store/products/products.actions';
import { Workflows } from '../../../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../../tracking/use-workflow-tracking';
import { useAdvancedShippingNoticeContext } from '../../advanced-shipping-notice-context';
import { DangerousGoods } from '../components/dangerous-goods';
import { SelectProductsSearchBar } from '../components/select-products-search-bar';
import { TableInformation } from '../components/table-information';
import { SELECTED_PRODUCTS, HAS_CONFIRMED_DANGEROUS_GOODS, DEFAULT_PRODUCT_QUANTITY } from '../constants';

export const SelectProductsEdit = ({ stepPayload, setStepPayload }) => {
  const { validationErrors, validatePayload, initialAsn, refreshAsnAfterImport, currentAsnId } = useAdvancedShippingNoticeContext();
  const hasBarcodeErrors = validationErrors?.selectedProducts?.some(
    ({ productOptions }) => !!productOptions?.[ShipStationFulfillmentBarcode],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const { isOpen: isImportDrawerOpen, open: openImportDrawer, close: closeImportDrawer } = useModal();
  const selectedProducts = stepPayload[SELECTED_PRODUCTS];
  const hasConfirmedDangerousGoods = stepPayload[HAS_CONFIRMED_DANGEROUS_GOODS];
  const productCount = selectedProducts?.length || 0;
  const hasProducts = productCount > 0;
  const dispatch = useDispatch();
  const selectedProductEntities = useSelectedProductEntities(stepPayload);

  const onImportSuccess = useCallback(async () => {
    await refreshAsnAfterImport();
  }, [refreshAsnAfterImport]);

  const handleProductSelected = useCallback(
    (product) => {
      dispatch(bulkLoadProductData([product.productId]));
      setStepPayload((prevPayload) => {
        const isProductAlreadySelected = prevPayload[SELECTED_PRODUCTS].some(
          (selectedProduct) => selectedProduct.productId === product.productId,
        );
        if (isProductAlreadySelected) {
          return prevPayload;
        }

        return {
          ...prevPayload,
          [SELECTED_PRODUCTS]: [...prevPayload[SELECTED_PRODUCTS], { ...product, quantity: DEFAULT_PRODUCT_QUANTITY }],
        };
      });
    },
    [setStepPayload, dispatch],
  );

  const handleProductDeleted = useCallback(
    (productId) => {
      setStepPayload((prevPayload) => {
        const newPayload = {
          ...prevPayload,
          [SELECTED_PRODUCTS]: prevPayload[SELECTED_PRODUCTS].filter((product) => product.productId !== productId),
        };
        validatePayload(newPayload, selectedProductEntities);
        return newPayload;
      });
    },
    [selectedProductEntities, setStepPayload, validatePayload],
  );

  const handleProductQuantityChange = useCallback(
    ({ productId, newQuantity }) => {
      setStepPayload((prevPayload) => ({
        ...prevPayload,
        [SELECTED_PRODUCTS]: prevPayload[SELECTED_PRODUCTS].map((product) =>
          product.productId === productId ? { ...product, quantity: newQuantity } : product,
        ),
      }));
    },
    [setStepPayload],
  );

  const handleClearProductsTable = useCallback(() => {
    setStepPayload((prevPayload) => {
      const newPayload = {
        ...prevPayload,
        [SELECTED_PRODUCTS]: [],
      };
      validatePayload(newPayload, selectedProductEntities);
      return newPayload;
    });
  }, [selectedProductEntities, setStepPayload, validatePayload]);

  const handleConfirmDangerousGoods = useCallback(() => {
    setStepPayload((prevPayload) => ({
      ...prevPayload,
      [HAS_CONFIRMED_DANGEROUS_GOODS]: !hasConfirmedDangerousGoods,
    }));
  }, [setStepPayload, hasConfirmedDangerousGoods]);

  const handleLearnMoreLink = () => {
    // TODO: SPD-19989 Implement learn more link
  };

  const trackWorkflow = useTrackWorkflow(Workflows.CSV_ASN_IMPORT_FLOW);
  const latestImport = useSelector((state) => Global.selectCsvImportType(state, ImportType.asn));

  const onOpen = () => {
    if (latestImport.status !== ImportStatus.ACTIVE) {
      trackWorkflow(WorkflowSteps.START, { version: 2 });
    }
    openImportDrawer();
  };

  return (
    <>
      {hasBarcodeErrors ? (
        <BannerMessageV2
          content={
            <>
              <Text textType={TextType.Label} value={messages.barcodeErrorHeader} />
              <Text textType={TextType.Paragraph} value={messages.barcodeErrorBody} />
            </>
          }
          layout={BannerLayout.Full}
          status={BannerStatus.Error}
        />
      ) : null}
      <div>
        <Text textType={TextType.Label} value={messages.addProductsToBeShippedDescription} />
        <FlexRow gap={Gap.Space16}>
          <SelectProductsSearchBar handleProductSelected={handleProductSelected} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Button onClick={onOpen} theme={THEME_CONSTANTS.tertiary}>
            <Text textType={TextType.Button} value={messages.addProductsCsvImportButton} />
          </Button>
        </FlexRow>
      </div>
      <TableInformation
        count={productCount}
        countInformationMessage={messages.productsAddedCount}
        handleClearAll={handleClearProductsTable}
      />
      <SelectProductsTable
        data={selectedProducts}
        handleProductDeleted={handleProductDeleted}
        handleProductQuantityChange={handleProductQuantityChange}
      />
      {hasProducts ? (
        <DangerousGoods
          handleConfirmDangerousGoods={handleConfirmDangerousGoods}
          handleLearnMoreLink={handleLearnMoreLink}
          hasConfirmedDangerousGoods={hasConfirmedDangerousGoods}
        />
      ) : null}
      {isImportDrawerOpen ? (
        <AsnImportDrawer
          asnId={currentAsnId || initialAsn.asnId}
          onClose={closeImportDrawer}
          onSuccess={onImportSuccess}
          trackProcess={trackWorkflow}
        />
      ) : null}
    </>
  );
};
